/* prettier-ignore */

import { Component, computed, inject, QueryList, signal, ViewChildren, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDrag, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CardTimeLineComponent } from './components/card/card-timeline.component';
import { NewCustomRouteService } from '../../new-custom-route.service';
import { PRIMARY_TIMELINE_COLORS, TIME_STRINGS } from '../../constants';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToolsService } from 'src/app/services/tools.service';

@Component({
  selector: 'map-timeline',
  imports: [
    CommonModule,
    DragDropModule,
    TranslateModule,
    CardTimeLineComponent
  ],
  standalone: true,
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class TimelineComponent {
  @ViewChildren("dropList") dropList: QueryList<CdkDropList>
  public totalAvailableHours: string[] = TIME_STRINGS;
  private routeService = inject(NewCustomRouteService);
  public translate = inject(TranslateService);
  public route = computed(() => this.routeService.route())
  public disableTouch = computed(() => this.routeService.disableTouch());
  public tools = inject(ToolsService);
  public canBeDraged = signal(false);
  
  public readonly PRIMARY_TIMELINE_COLORS = PRIMARY_TIMELINE_COLORS;
  private readonly NO_SPACE_CLASS = 'timeline-card__no-space';
  private readonly FREE_SPACE_CLASS = 'timeline-card__free-space';
  
    drop(event: any, day){
      if (!this.canBeDraged()){
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        this.routeService.moveItemInSameDay(day);
        this.routeService.setDay(day)
      }
      this.routeService.checkRightTimes();
      this.routeService.reloadMap();
    }

    onCardDrop(event: any, item: any, listIndex: any) {
      if (!this.canBeDraged()) {
        this.resetTimelineCardStyles(); 
        return;
      };     

      const targetElement = document.elementFromPoint(event.dropPoint.x,event.dropPoint.y)?.closest('.card-item') as HTMLDivElement;
      if (!targetElement) 
        return;

      const itemId = targetElement.getAttribute('index')
      const listId = targetElement.parentElement.getAttribute('index')

      this.routeService.removeItem(item.point);
      this.routeService.moveItem(listId, itemId, item);
      this.routeService.setDay(parseInt(listId))
    }

    allowEnter(drag: CdkDrag, drop: CdkDropList): boolean {
      return false;
    }

    onDragMove(event: any, item: any): void {
      this.canBeDraged.set(false);
      this.setPreviewStyle(0.8);
      this.resetTimelineCardStyles();
    
      const targetElement = this.getTargetElement(event.pointerPosition);
      if (!targetElement) return;
    
      const { cardItem, parentIndex, itemIndex } = this.getTargetIndices(targetElement);
      const currentItemIndex = this.getCurrentItemIndex(event);
      if (parentIndex === currentItemIndex) return;
    
      const foundItem = this.route()?.[parseInt(parentIndex)]?.points?.[parseInt(itemIndex)];
      if (this.isValidDirectMove(foundItem, item)) {
        targetElement.classList.add(this.FREE_SPACE_CLASS);
        this.canBeDraged.set(true);
      } 
      else if (foundItem?.type === 'add') {
        this.handleAddMove(cardItem, parentIndex, itemIndex, item);
      } 
      else {
        targetElement.classList.add(this.NO_SPACE_CLASS);
        this.canBeDraged.set(false);
      }
    }
    
    private setPreviewStyle(opacity: number): void {
      const preview = document.querySelector('.cdk-drag-preview') as HTMLElement;
      if (preview) {
        preview.style.opacity = `${opacity}`;
        const currentTransform = preview.style.transform;
        const translateMatch = currentTransform.match(/translate3d\([^)]+\)/);
        const translatePart = translateMatch ? translateMatch[0] : '';
        preview.style.transform = `${translatePart} scale(1.05) rotate(5deg)`;
        const time = preview.querySelector('.time') as HTMLElement;
        if (time)
          time.style.display = 'none';

        const removeIcon = preview.querySelector('.remove-icon') as HTMLElement;
        if (removeIcon)
          removeIcon.style.display = 'none';
      }
    }
    
    private getTargetElement(pointerPosition: { x: number; y: number }): HTMLElement | null {
      return document.elementFromPoint(pointerPosition.x, pointerPosition.y)?.closest('.card-item')?.querySelector('.timeline-card') as HTMLDivElement || null;
    }
    
    private getTargetIndices(targetElement: HTMLElement): { cardItem: HTMLElement; parentIndex: string; itemIndex: string } {
      const cardItem = targetElement.closest('.card-item') as HTMLElement;
      const parentElement = cardItem.parentElement as HTMLElement;
      const parentIndex = parentElement.getAttribute('index') || '';
      const itemIndex = cardItem.getAttribute('index') || '';
      return { cardItem, parentIndex, itemIndex };
    }
    
    private getCurrentItemIndex(event: any): string {
      return event.source.dropContainer.element.nativeElement.getAttribute('index') || '';
    }
    
    private isValidDirectMove(foundItem: any, item: any): boolean {
      return foundItem?.point?.size >= item.point?.size && foundItem.type !== 'add';
    }
    
    private handleAddMove(cardItem: HTMLElement, parentIndex: string, itemIndex: string, item: any): void {
      this.resetTimelineCardStyles();
      const size = parseInt(item.point?.size || '0', 10);
      const arrayAdds: HTMLElement[] = [];
      let currentItem = cardItem;
      let canBeMoved = true;
    
      for (let i = parseInt(itemIndex, 10); i < parseInt(itemIndex, 10) + size; i++) {
        arrayAdds.push(currentItem);
        currentItem = currentItem?.nextElementSibling as HTMLElement;
        if (this.route()?.[parseInt(parentIndex, 10)]?.points?.[i]?.type !== 'add') {
          canBeMoved = false;
          break;
        }
      }
    
      if (canBeMoved) {
        arrayAdds.forEach((element) => {
          const test = element.querySelector('.timeline-card');
          if (test) test.classList.add('timeline-card__free-space');
        });
        this.canBeDraged.set(true);
      } else {
        cardItem.querySelector('.timeline-card')?.classList.add('timeline-card__no-space');
        this.canBeDraged.set(false);
      }
    }

    onDragReleased(event: any, item: any){
      if (!this.canBeDraged()) {
        this.resetTimelineCardStyles();
        return
      };

      const placeholder = document.querySelector(".cdk-drag-placeholder") as HTMLElement;
      if (placeholder) placeholder.style.cssText = 'animation: none !important; ';
      placeholder?.remove()
      
      const preview = document.querySelector(".cdk-drag-preview") as HTMLElement;
      if (preview) preview.style.cssText = 'display: none !important; animation: none !important;';
      preview?.remove()
    }

    private resetTimelineCardStyles(): void {
      const allItems = document.querySelectorAll('.timeline-card');
      allItems.forEach((item: HTMLDivElement) => {
        item.classList.remove(this.NO_SPACE_CLASS, this.FREE_SPACE_CLASS);
      });
    }

    addItem(dayIndex, pointIndex){
      this.routeService.modalAddItem(dayIndex, pointIndex);
    }

}